import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { ButtonOutline } from '../components/Button';

const ENGINE_GREEN = '#00FF88';

const cardGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0,255,136,0.08); }
  50%       { box-shadow: 0 0 40px rgba(0,255,136,0.22); }
`;

const Section = styled.section`
  padding: 140px 80px;
  overflow: hidden;
  @media (max-width: 900px) { padding: 80px 24px; }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
  @media (max-width: 600px) { margin-bottom: 48px; }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;
  margin-bottom: 20px;
  color: ${ENGINE_GREEN};
  text-shadow: 0 0 60px rgba(0,255,136,0.35);
`;

const Subtitle = styled.p`
  font-size: 17px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 600;
`;

const Desc = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 680px;
  margin: 0 auto 48px;
  line-height: 1.82;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 64px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid rgba(0,255,136,0.1);
  padding: 32px 28px;
  transition: border-color 0.4s, transform 0.4s;

  &:hover {
    border-color: rgba(0,255,136,0.38);
    transform: translateY(-5px);
    animation: ${cardGlow} 2s ease-in-out infinite;
  }
`;

const FeatureIcon = styled.div`
  font-size: 42px;
  margin-bottom: 18px;
  filter: drop-shadow(0 0 14px rgba(0,255,136,0.35));
`;

const FeatureTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 13px;
  letter-spacing: 0.14em;
  color: ${ENGINE_GREEN};
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const FeatureDesc = styled.p`
  font-size: 14px;
  line-height: 1.78;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Actions = styled.div`
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ButtonGreen = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 15px 38px;
  clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
  transition: all 0.3s ease;
  background: linear-gradient(135deg, ${ENGINE_GREEN}, #00CC66);
  color: #000;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 0 40px rgba(0,255,136,0.4);
  }

  @media (max-width: 480px) { padding: 13px 28px; font-size: 12px; }
`;

export default function EngineShowcase() {
  const { t } = useTranslation();
  const features = t('engineShowcase.features', { returnObjects: true });

  return (
    <Section id="engine-showcase">
      <Inner>
        <AnimatedSection>
          <Header>
            <SectionLabel>{t('engineShowcase.sectionLabel')}</SectionLabel>
            <Title>{t('engineShowcase.title')}</Title>
            <Subtitle>{t('engineShowcase.subtitle')}</Subtitle>
            <Desc>{t('engineShowcase.desc')}</Desc>
          </Header>
        </AnimatedSection>

        <Grid>
          {features.map((f, i) => (
            <AnimatedSection key={i} delay={i * 80}>
              <FeatureCard>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            </AnimatedSection>
          ))}
        </Grid>

        <AnimatedSection>
          <Actions>
            <ButtonGreen
              href="https://github.com/GueblyGames/guebly-engine/releases/latest"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⬇ &nbsp;{t('engineShowcase.btnDownload')}
            </ButtonGreen>
            <ButtonOutline
              as={Link}
              to="/engine"
            >
              {t('engineShowcase.btnDocs')}
            </ButtonOutline>
          </Actions>
        </AnimatedSection>
      </Inner>
    </Section>
  );
}
